import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageMeta from '@/components/base/PageMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, articles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];
  const allCategoryArticles = articles.filter((a) => a.category === activeCategory);

  return (
    <>
      <ScrollToTop />
      <PageMeta
        title={`${currentCategory.name} - 海角社区地址导航`}
        description={`海角社区${currentCategory.name}相关内容，${currentCategory.description}。海角社区地址导航为您整理最新最全的访问指南。`}
        canonicalUrl={`https://haijiao-navi.readdy.ai/category/${activeCategory}`}
        jsonLdScripts={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory.name} - 海角社区地址导航`,
            description: `海角社区${currentCategory.name}相关内容汇总`,
            url: `https://haijiao-navi.readdy.ai/category/${activeCategory}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '海角社区地址导航',
              url: 'https://haijiao-navi.readdy.ai/',
            },
          },
        ]}
      />

      <Navbar />
      <Breadcrumb />

      <main>
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className={`${currentCategory.icon} text-lg`}></i>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                {currentCategory.name}
              </h1>
            </div>
            <p className="text-sm text-foreground-600 ml-[52px]">
              {currentCategory.description}——共 {allCategoryArticles.length} 篇文章
            </p>
          </div>
        </section>

        <section className="py-8 md:py-10 bg-background-50">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {allCategoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {allCategoryArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-600">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}